import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../components/controls/modal/Modal";
import Input from "../../components/controls/input/customInput/CustomInput";
import UserSelect from "../../components/userSelect/UserSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
    createRunbookActionRequest,
    updateRunbookActionRequest,
    getRunbookActionDetailRequest,
} from "../../store/actions/runbooks.actions";
import {
    runbookDetailLoading,
    runbookActionDetail,
    runbookActionDetailLoading,
} from "../../store/selectors/runbooks.selector";
import {
    RunbookActionStatus,
    formatDuration,
    UserSearchRefType,
    getRunbookActionStatusLabel,
} from "../../helpers/utils";
import { calcDurationMinutes, ACTION_STATUS_OPTIONS, INITIAL_ACTION_FORM } from "./utils";
import InputUnderline from "../../components/controls/input/InputUnderline";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import runbooksService from "../../store/services/runbooks.service";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";

const RunbookActionModal = ({ id, projectId, runbookId, onSave, onClose }) => {
    const dispatch = useDispatch();
    const actionDetail = useSelector(runbookActionDetail);
    const isLoading = useSelector(runbookActionDetailLoading);
    const isEdit = !!id;

    const [form, setForm] = useState(INITIAL_ACTION_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [plannedDuration, setPlannedDuration] = useState(null);
    const [actualDuration, setActualDuration] = useState(null);


    useEffect(() => {
        setPlannedDuration(calcDurationMinutes(form.plannedStartDate, form.plannedEndDate));
    }, [form.plannedStartDate, form.plannedEndDate]);

    useEffect(() => {
        setActualDuration(calcDurationMinutes(form.actualStartDate, form.actualEndDate));
    }, [form.actualStartDate, form.actualEndDate]);

    // Fetch action details when editing
    useEffect(() => {
        if (isEdit) {
            dispatch(getRunbookActionDetailRequest({ runbookId, actionId: id }));
        }
    }, [isEdit]);

    // Populate form once detail is loaded
    useEffect(() => {
        if (actionDetail && isEdit && actionDetail.id === id) {
            setForm({
                title: actionDetail.title,
                refId: actionDetail.refId || "",
                description: actionDetail.description,
                order: actionDetail.order ?? 1,
                plannedStartDate: actionDetail.plannedStartDate,
                plannedEndDate: actionDetail.plannedEndDate,
                actualStartDate: actionDetail.actualStartDate,
                actualEndDate: actionDetail.actualEndDate,
                status: actionDetail.status ?? RunbookActionStatus.Pending,
                notes: actionDetail.notes,
                assignees: actionDetail.assignees || [],
                attachments: actionDetail.attachments || [],
            });
        }
    }, [actionDetail]);


    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const validate = () => {
        const errs = {};
        if (!form.title?.trim()) errs.title = "Title is required";
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const showActual = isEdit && form.status >= RunbookActionStatus.InProgress;

        const payload = {
            title: form.title.trim(),
            refId: form.refId?.trim(),
            description: form.description,
            order: form.order,
            plannedStartDate: form.plannedStartDate,
            plannedEndDate: form.plannedEndDate,
            actualStartDate: showActual ? form.actualStartDate : null,
            actualEndDate: showActual ? form.actualEndDate : null,
            notes: form.notes,
            assigneeIds: form.assignees ? form.assignees.map((a) => a.id) : [],
        };

        if (isEdit) {
            dispatch(updateRunbookActionRequest({
                runbookId,
                actionId: id,
                payload: { ...payload, status: form.status },
                onSuccess: () => { onClose(); onSave(); }
            }));
        } else {
            dispatch(createRunbookActionRequest({
                runbookId,
                payload,
                onSuccess: () => { onClose(); onSave(); }
            }));
        }
    };

    const showActual = isEdit && form.status >= RunbookActionStatus.InProgress;
    const modalBody = (
        <div className="d-flex flex-column gap-3">
            <div className="row gx-3 align-items-end">
                <div className={"col-12"}>
                    <InputUnderline
                        maxLength={255}
                        placeholder="Enter title"
                        value={form.title}
                        handleChange={(v) => update("title", v)}
                        error={formErrors.title}
                        isRequired
                        loading={isLoading}
                    />
                </div>
            </div>
            <div className="row gx-3 align-items-end">
                <div className="col-4">
                    <Input
                        icon={false}
                        placeholder="Enter Change Ticket"
                        value={form.refId}
                        maxLength={255}
                        type="text"
                        handleChange={(v) => update("refId", v)}
                        label="Change Ticket"
                        loading={isLoading}
                    />
                </div>
                {
                    isEdit && (
                        <div className="col-4">
                            <DropDown
                                label="Status"
                                options={ACTION_STATUS_OPTIONS}
                                defaultVal={getRunbookActionStatusLabel(form.status)}
                                onChange={(val) => update("status", val)}
                                loading={isLoading}
                            />
                        </div>
                    )
                }
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <UserSelect
                        multiple={true}
                        refType={UserSearchRefType.Project}
                        refId={projectId}
                        value={form.assignees}
                        label="Assignees"
                        buttonLabel={"Select Assignees"}
                        onChange={(selected) => {
                            update("assignees", selected || []);
                        }}
                        loading={isLoading}
                    />
                </div>
            </div>
            <div className="row gx-3 align-items-end">
                <div className="col">
                    <Input
                        label="Planned Start"
                        value={form.plannedStartDate}
                        type="datetime"
                        handleChange={(v) => { update("plannedStartDate", v); }}
                        maxDate={form.plannedEndDate}
                        loading={isLoading}
                    />

                </div>
                <div className="col">
                    <Input
                        label="Planned End"
                        value={form.plannedEndDate}
                        type="datetime"
                        handleChange={(v) => update("plannedEndDate", v)}
                        minDate={form.plannedStartDate}
                        loading={isLoading}
                    />

                </div>
                <div className="col-2">
                    <label className="Input_label">Duration</label>
                    <p
                        style={{ border: "1px solid #342C82" }}
                        className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                    >
                        {plannedDuration !== null ? formatDuration(plannedDuration) : "-"}
                    </p>
                </div>
            </div>

            {showActual && (
                <div className="row gx-3 align-items-end">
                    <div className="col">
                        <Input
                            label="Actual Start"
                            value={form.actualStartDate}
                            type="datetime"
                            handleChange={(v) => update("actualStartDate", v)}
                            maxDate={form.actualEndDate}
                            loading={isLoading}
                        />

                    </div>
                    <div className="col">
                        <Input
                            label="Actual End"
                            value={form.actualEndDate}
                            type="datetime"
                            handleChange={(v) => update("actualEndDate", v)}
                            minDate={form.actualStartDate}
                            loading={isLoading}
                        />

                    </div>
                    <div className="col-2">
                        <label className="Input_label">Duration</label>
                        <p
                            style={{ border: "1px solid #342C82" }}
                            className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                        >
                            {actualDuration ? formatDuration(actualDuration) : "-"}
                        </p>
                    </div>
                </div>
            )}

            <div className="row gx-3">
                <div className="col-12">
                    <RichTextEditor
                        label="Description"
                        content={form.description}
                        onChange={(v) => update("description", v)}
                        loading={isLoading}
                    />
                </div>
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <RichTextEditor
                        label="Notes"
                        content={form.notes}
                        onChange={(v) => update("notes", v)}
                        loading={isLoading}
                    />
                </div>
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <EntityFileUpload
                        entityId={id}
                        entityType="RunbookAction"
                        attachments={form.attachments}
                        onAttachmentsChange={() => {
                            if (id) {
                                runbooksService.getRunbookActionDetailRequest({ runbookId: runbookId, actionId: id })
                                    .then((runbookActionData) => {
                                        update("attachments", runbookActionData.attachments);
                                    })
                                    .catch((error) => {
                                        console.error("Error fetching runbook action data:", error);
                                    });
                            }
                        }}
                        disabled={isLoading || !id}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            title={isEdit ? "Edit Action" : "New Action"}
            body={modalBody}
            handleCancel={onClose}
            handleSave={handleSave}
            saveBtnLabel={isEdit ? "Update" : "Create"}
            cancelBtnLabel="Cancel"
            size="large"
        />
    );
};

export default RunbookActionModal;
